// "+ Add another medication" picker for the proactive medication SuggestionCard
// (MEDICATION_BATCH_LOGGING_PLAN.md Phase 3). The picker UI itself lives in the shared
// MedPickerModal; this wrapper only supplies what to do with the picked medications:
// fold them into the pending suggestion through the add-to-batch endpoint.

import { addMedicationToSuggestion, type ProactiveSuggestion } from "../lib/api.js";
import { MedPickerModal, type MedPickerProgress } from "./MedPickerModal.js";
import type { MedChoice } from "./medPicker.js";

export function AddMedToSuggestionModal({
  patientId,
  excludeNames,
  pendingId,
  keepIndexes,
  onClose,
  onAdded,
  onError,
}: {
  patientId: string;
  /** Lowercased names already present in the current suggestion (D-4). */
  excludeNames: Set<string>;
  pendingId: string;
  /** Currently-kept indices into the original pending's events (D-7). */
  keepIndexes?: number[];
  onClose: () => void;
  onAdded: (updated: ProactiveSuggestion) => void;
  onError: (message: string) => void;
}) {
  // Adds each pick one at a time, chaining each call's returned pendingId into the next
  // (the add-to-batch endpoint folds one med into an existing pending at a time — see
  // api.ts addMedicationToSuggestion). Only the first call needs `keepIndexes` (which
  // original rows to keep); once the batch has been rebuilt once, later calls keep
  // everything already in it.
  async function addMeds(meds: MedChoice[], report: (p: MedPickerProgress) => void) {
    let currentPendingId = pendingId;
    let currentKeepIndexes = keepIndexes;
    let updated: ProactiveSuggestion | null = null;
    for (let i = 0; i < meds.length; i++) {
      const med = meds[i]!;
      report({ name: med.name, current: i + 1, total: meds.length });
      updated = await addMedicationToSuggestion(patientId, currentPendingId, med.regimenId, med.lineId, currentKeepIndexes);
      currentPendingId = updated.pendingId;
      currentKeepIndexes = undefined;
    }
    if (updated) onAdded(updated);
  }

  return (
    <MedPickerModal
      patientId={patientId}
      excludeNames={excludeNames}
      onSelect={addMeds}
      onClose={onClose}
      onError={onError}
    />
  );
}
